import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toaster } from "@/components/ui/toaster";
import { useAuth } from "@/hooks/useAuth";
import { AxiosError } from "axios";
import { CheckRequestDetail } from "../schema/check-requests.schema";
import {
  getCheckRequest,
  updateCheckRequest,
  UpdateCheckRequestProps,
} from "@/services/check-requests.service";
import { CheckRequestAmountAndActions } from "@/components/common/section-components/requests/check/CheckRequestAmountAndActions";
import { CheckRequestMainDataCard } from "@/components/common/section-components/card/CheckRequestMainDataCard";
import { CheckRequestDetailLegend } from "@/components/common/section-components/requests/check/CheckRequestDetailLegend";
import { CheckRequestMessageList } from "@/components/common/section-components/requests/check/CheckRequestMessageList";
import { RequestSectionHeaderTitle } from "@/components/common/section-components/requests/RequestSectionHeaderTitle";
import { RequestMovementNumber } from "@/components/common/section-components/requests/RequestMovementNumber";
import { handlePermissionError } from "@/utils/permission-check";
import { useRoles } from "@/hooks/useRoles";
import { editAmountHandler } from "./handler-functions";
import { validateMinCurrencyAmount } from "@/utils/request-validations";
import { EstadosCheque, EstadosECheq } from "@/constants/enums/status/entity-status-ids";
import { RequestTypes } from "@/constants/enums";

export const CheckRequestDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();

  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.CHECK_REQUESTS);
  };

  const [loadingRequest, setLoadingRequest] = useState<boolean>(false);

  const [request, setRequest] = useState<CheckRequestDetail | null>();

  const fetchRequest = async (showLoad: boolean = true) => {
    try {
      if (showLoad) setLoadingRequest(true);
      const response = await getCheckRequest({
        id: Number(id),
      });

      setRequest(response);

      setLoadingRequest(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title: errorObj?.message ?? "No se pudo cargar la solicitud.",
        });
      }
      throw error;
    }
  };

  useEffect(() => {
    if (isActive && id && user) fetchRequest();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);

  const { fetchPermissions } = useRoles();
  const handleEditAmount = async () => {
    editAmountHandler({
      request,
      errors,
      getValues,
      fetchRequest,
      setLoadingEditAmount,
      setIsEditingAmount,
      navigate,
      fetchPermissions,
    });
  };

  const radioItems = [
    { label: "Usted", color: AppColors.BLACK_TEXT_SECONDARY },
    { label: "Usuario", color: AppColors.WHITE_BORDER_1 },
  ];

  const {
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    getValues,
  } = useForm({
    resolver: zodResolver(
      z.object({
        request_amount: z.any().optional(),
      }),
    ),
    defaultValues: {
      request_amount: request?.montoSolicitado,
    },
  });

  const { open: openInfoModal, setOpen: setOpenInfoModal } = useDisclosure();

  const { open: openRejectModal, setOpen: setOpenRejectModal } =
    useDisclosure();

  const { open: openExecuteModal, setOpen: setOpenExecuteModal } =
    useDisclosure();

  const [loadingSendInfo, setLoadingSendInfo] = useState<boolean>(false);
  const [loadingRejectRequest, setLoadingRejectRequest] =
    useState<boolean>(false);
  const [loadingAcceptRequest, setLoadingAcceptRequest] =
    useState<boolean>(false);
  const [loadingExecuteRequest, setLoadingExecuteRequest] =
    useState<boolean>(false);

  const watchedValues = useWatch({
    control,
  });

  const handleSendInfo = async (event?: FieldValues) => {
    setLoadingSendInfo(true);
    try {
      const objToSend: UpdateCheckRequestProps = {
        idSolicitud: request?.id ?? 0,
        estado: request?.idTipoSolicitud == RequestTypes.Cheque ? EstadosCheque.RequiereInformacion : EstadosECheq.RequiereInformacion,
        mensaje: event?.request_detail,
        idMotivo: event?.message_motive?.[0],
      };

      await updateCheckRequest(objToSend);

      toaster.create({
        type: "success",
        title: "Pedido de información realizado exitosamente",
      });

      await fetchRequest(false);
      setLoadingSendInfo(false);
    } catch (error) {
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
        AppRoutes.CHECK_REQUEST_DETAIL.replace(":id", String(id)),
      );
      if (!handled) {
        const errorObj = error as AxiosError<{ message: string }>;
        toaster.create({
          type: "error",
          title:
            errorObj?.response?.data?.message ??
            "Hubo un error al enviar el pedido de información.",
        });
      }
    } finally {
      setLoadingSendInfo(false);
    }

    setOpenInfoModal(false);
  };

  const handleRejectRequest = async (event?: FieldValues) => {
    setLoadingRejectRequest(true);
    try {
      const objToSend: UpdateCheckRequestProps = {
        idSolicitud: Number(request?.id),
        estado: request?.idTipoSolicitud == RequestTypes.Cheque ? EstadosCheque.Rechazada : EstadosECheq.Rechazada,
        mensaje: event?.request_detail,
        idMotivo: event?.message_motive?.[0],
      };

      await updateCheckRequest(objToSend);

      toaster.create({
        type: "success",
        title: "Solicitud rechazada exitosamente.",
      });

      await fetchRequest(false);
      setLoadingRejectRequest(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
        AppRoutes.CHECK_REQUEST_DETAIL.replace(":id", String(id)),
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.response?.data?.message ??
            "No se pudo rechazar la solicitud.",
        });
        throw error;
      }
    } finally {
      setLoadingRejectRequest(false);
    }
    setOpenRejectModal(false);
  };

  const handleExecuteModal = () => {
    setOpenExecuteModal(true);
  };

  const handleExecuteRequest = async (event?: FieldValues) => {
    setLoadingExecuteRequest(true);

    try {
      const objToSend: UpdateCheckRequestProps = {
        idSolicitud: request?.id ?? 0,
        estado: request?.idTipoSolicitud == RequestTypes.Cheque ? EstadosCheque.Disponible : EstadosECheq.Disponible,
        nroCheque: event?.check_number,
      };

      await updateCheckRequest(objToSend);

      toaster.create({
        type: "success",
        title: `Solicitud ejecutada exitosamente.`,
      });

      await fetchRequest(false);

      setOpenExecuteModal(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
        AppRoutes.CHECK_REQUEST_DETAIL.replace(":id", String(id)),
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.response?.data?.message ??
            "No se pudo ejecutar la solicitud.",
        });
        throw error;
      }
    }
    setOpenExecuteModal(false);
    setLoadingExecuteRequest(false);
  };

  const handleAcceptRequest = async (status: EstadosCheque | EstadosECheq) => {
    setLoadingAcceptRequest(true);
    try {
      const objToSend: UpdateCheckRequestProps = {
        idSolicitud: request?.id ?? 0,
        estado: status,
      };

      await updateCheckRequest(objToSend);

      const statusString =
        status === EstadosCheque.Disponible ? "ejecutada" : "finalizada";
      toaster.create({
        type: "success",
        title: `Solicitud ${statusString} exitosamente.`,
      });

      await fetchRequest(false);
    } catch (error) {
      const statusString =
        status === EstadosCheque.Disponible ? "ejecutar" : "finalizar";
      toaster.create({
        type: "error",
        title: `No se pudo ${statusString} la solicitud.`,
      });
      throw error;
    }

    setLoadingAcceptRequest(false);
  };

  useEffect(() => {
    if (request) {
      setValue("request_amount", request?.montoSolicitado);
    }
  }, [request]);

  useEffect(() => {
    validateMinCurrencyAmount({
      watchedAmount: watchedValues?.request_amount,
      clearErrors,
      setError,
      fieldName: "request_amount",
      min: 1,
    })
  }, [watchedValues?.request_amount, errors]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={<RequestSectionHeaderTitle request={request} type="Cheque" />}
        onBack={handleBack}
        backButton
        loading={
          loadingRequest ||
          loadingRejectRequest ||
          loadingSendInfo ||
          loadingAcceptRequest ||
          loadingExecuteRequest
        }
      />

      <RequestMovementNumber request={request} />
      {!loadingRequest && (
        <>
          <CheckRequestAmountAndActions
            control={control}
            isEditingAmount={isEditingAmount}
            loadingEditAmount={loadingEditAmount}
            loadingRejectRequest={loadingRejectRequest}
            loadingAcceptRequest={loadingAcceptRequest}
            loadingRequest={loadingRequest}
            loadingSendInfo={loadingSendInfo}
            request={request}
            setOpenInfoModal={setOpenInfoModal}
            setOpenRejectModal={setOpenRejectModal}
            handleAcceptRequest={handleAcceptRequest}
            handleEditAmount={handleEditAmount}
            setIsEditingAmount={setIsEditingAmount}
            handleExecuteModal={handleExecuteModal}
            setValue={setValue}
            clearErrors={clearErrors}
            errors={errors}
          />

          <CheckRequestMainDataCard request={request} />

          <Flex
            flexDir="column"
            padding="12px"
            height="100%"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="12px"
            gap="25px"
          >
            <CheckRequestDetailLegend radioItems={radioItems} />

            <CheckRequestMessageList request={request} />
          </Flex>
        </>
      )}

      {request?.id && (
        <Suspense>
          <NeedInfoModalDynamic
            open={openInfoModal}
            setOpen={setOpenInfoModal}
            handleAccept={handleSendInfo}
            loadingAccept={loadingSendInfo}
            idTipoSolicitud={request?.idTipoSolicitud}
          />
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectRequest}
            loadingAccept={loadingRejectRequest}
            idTipoSolicitud={request?.idTipoSolicitud}
          />
          <ExecuteCheckRequestModalDynamic
            open={openExecuteModal}
            setOpen={setOpenExecuteModal}
            handleAccept={handleExecuteRequest}
            loadingAccept={loadingExecuteRequest}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const NeedInfoModalDynamic = lazy(() =>
  import("@/components/modal/NeedInfoModal").then((el) => ({
    default: el["NeedInfoModal"],
  })),
);

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);

const ExecuteCheckRequestModalDynamic = lazy(() =>
  import("@/components/modal/ExecuteCheckRequestModal").then((el) => ({
    default: el["ExecuteCheckRequestModal"],
  })),
);
